// @ts-nocheck
/*
 * Port of views/dashboard/ivr/IVR/nodes/TransferNode.js (820 lines).
 * BYTE-IDENTICAL — reactflow → @xyflow/react, AudioPreviewModel → AudioPreviewModal.
 *
 * Preserves verbatim: timeSlots wire shape `{ start_time, end_time,
 * type: "transfer_group"|"transfer_agent", id, name }`, day-coverage
 * math (overnight en <= st bumps by 1440, FULL_DAY_MINUTES = 1440),
 * legacy migration of pre-existing detached `defaultNode` child →
 * inline `defaultAction`, `defaultNodeId: null` reset on save,
 * `defaultAction` shape (mode/action/max_duration/use_media/
 * recordSilenceStop/recordStopDtmf) matching v1 exactly for the
 * voicemail branch, `fullDay` → `defaultAction: null` short-circuit,
 * Save-button disabled when any slot is incomplete or overlapping.
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Handle, useReactFlow } from '@xyflow/react';
import { Button, Form } from './_rbShim';
import { NodesContext } from '../provider/IvrContext';
import { toast } from 'sonner';
import { PhoneForwarded, Settings, Trash2, ArrowRight } from 'lucide-react';
import IvrDrawer from '../Helpers/IvrDrawer';
import AudioPreviewModal from '../Helpers/AudioPreviewModal';
import { triggerHandleConnect, findUpstreamLanguageNode } from '../Helpers/utils';
import { getApiCall } from '@/service/httpCall';
import { API_AGENTS_LIST, API_AGENT_GROUPS_LIST } from '@/service/url';
import { CalendarTimeInput } from '@/components/ui/calendar-inputs';
import {
  BACKEND_API_BASE_URL,
  VOICEMAIL_MAX_DURATION,
  VOICEMAIL_MIN_DURATION,
  TRANSFER_DEFAULT_OPTIONS,
} from '@/utilities/constant';

const convertToMinutes = (time: any) => {
  if (!time || !time.includes(':')) return 0;
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

const FULL_DAY_MINUTES = 1440;
const DAY_START = '00:00';
const DAY_END = '23:59';

const isFullDaySlot = (s: any) => s?.startTime === DAY_START && s?.endTime === DAY_END;

const computeCoverage = (slots: any[]) =>
  slots.reduce((acc: number, s: any) => {
    if (!s.startTime || !s.endTime) return acc;

    const st = convertToMinutes(s.startTime);
    // 23:59 is the last pickable minute — count it as running to midnight,
    // otherwise a "Full day" slot covers 1439/1440 and still demands a
    // default action.
    let en = s.endTime === DAY_END ? FULL_DAY_MINUTES : convertToMinutes(s.endTime);

    // overnight slot handling
    if (en <= st) {
      en += 1440;
    }

    return acc + (en - st);
  }, 0);

const TransferNode = ({ data, id }: any) => {
  const { updateNodeData, onRemove } = useContext(NodesContext) as any;
  const { setNodes, setEdges, getNode, getNodes, getEdges } = useReactFlow();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [groupList, setGroupList] = useState<any[]>([]);
  const [agentList, setAgentList] = useState<any[]>([]);
  const [promptList, setPromptList] = useState<any[]>([]);

  const [timeSlots, setTimeSlots] = useState<any[]>(
    data.timeSlots?.length
      ? data.timeSlots.map((slot: any) => ({
          startTime: slot.start_time || slot.startTime || '',
          endTime: slot.end_time || slot.endTime || '',
          type: slot.type?.replace('transfer_', '') || '',
          transferId: slot.id || '',
        }))
      : [
          {
            startTime: '',
            endTime: '',
            type: '',
            transferId: '',
          },
        ],
  );

  const initialDefault = data.defaultAction || {};
  const [answerLangCodes, setAnswerLangCodes] = useState<any[]>([]);
  const [hasLangParent, setHasLangParent] = useState(false);
  const [draftDefaultMode, setDraftDefaultMode] = useState(
    initialDefault.mode || '',
  );
  const [draftDefaultAction, setDraftDefaultAction] = useState<any>(
    initialDefault.action || {},
  );
  const [draftDefaultLanguage, setDraftDefaultLanguage] = useState(
    (initialDefault.action || {}).language || '',
  );
  const [draftDefaultCategory, setDraftDefaultCategory] = useState('general');
  const [draftDefaultDuration, setDraftDefaultDuration] = useState(
    initialDefault.max_duration || '',
  );
  const [draftDefaultUseMedia, setDraftDefaultUseMedia] = useState(
    Boolean(initialDefault.use_media),
  );
  const [draftDefaultSilenceStop, setDraftDefaultSilenceStop] = useState<any>(
    initialDefault.recordSilenceStop ?? 0,
  );
  const [draftDefaultStopDtmf, setDraftDefaultStopDtmf] = useState<any>(
    initialDefault.recordStopDtmf ?? 'any_dtmf',
  );
  const [defaultErrors, setDefaultErrors] = useState<any>({});
  const [drawerPreview, setDrawerPreview] = useState<any>({
    open: false,
    url: '',
    name: '',
  });

  const coverageMins = useMemo(() => computeCoverage(timeSlots), [timeSlots]);
  const isFullDay = coverageMins >= FULL_DAY_MINUTES;

  const savedSlots = data.timeSlots || [];
  const savedCoverage = useMemo(
    () =>
      computeCoverage(
        savedSlots.map((s: any) => ({
          startTime: s.start_time || s.startTime || '',
          endTime: s.end_time || s.endTime || '',
        })),
      ),
    [savedSlots],
  );
  const savedFullDay = savedCoverage >= FULL_DAY_MINUTES;

  const addTimeSlot = () => {
    setTimeSlots((prev) => [
      ...prev,
      { startTime: '', endTime: '', type: '', transferId: '' },
    ]);
  };

  const removeTimeSlot = (index: number) => {
    setTimeSlots((prev) => prev.filter((_: any, i: number) => i !== index));
  };

  const updateTimeSlot = (index: number, key: string, value: any) => {
    const updated = [...timeSlots];
    updated[index][key] = value;
    if (key === 'type') updated[index].transferId = '';
    setTimeSlots(updated);
  };

  // Full day is derived from the times (00:00–23:59), so it round-trips
  // through the existing start_time/end_time wire shape with no new field.
  const setSlotFullDay = (index: number, on: boolean) => {
    setTimeSlots((prev: any[]) =>
      prev.map((s: any, i: number) =>
        i === index
          ? { ...s, startTime: on ? DAY_START : '', endTime: on ? DAY_END : '' }
          : s,
      ),
    );
  };

  const isTimeOverlapping = (currentIndex: number, start: any, end: any) => {
    if (!start || !end) return false;

    let startMinutes = convertToMinutes(start);
    let endMinutes = convertToMinutes(end);

    if (endMinutes <= startMinutes) {
      endMinutes += 1440;
    }

    return timeSlots.some((slot: any, index: number) => {
      if (index === currentIndex) return false;
      if (!slot.startTime || !slot.endTime) return false;

      let slotStart = convertToMinutes(slot.startTime);
      let slotEnd = convertToMinutes(slot.endTime);

      if (slotEnd <= slotStart) {
        slotEnd += 1440;
      }

      return startMinutes < slotEnd && endMinutes > slotStart;
    });
  };

  useEffect(() => {
    getApiCall(API_AGENT_GROUPS_LIST, { fields: 'min' }).then(setGroupList as any).catch(console.log);
    getApiCall(API_AGENTS_LIST, { fields: 'min' }).then(setAgentList as any).catch(console.log);
    if (data.userId) {
      getApiCall(`${BACKEND_API_BASE_URL}/v2/prompt?user_id=${data.userId}`)
        .then((resp: any) => {
          if (Array.isArray(resp)) {
            setPromptList(
              resp.filter(
                (p: any) =>
                  p.prompt_category === 'general' &&
                  Array.isArray(p.prompt_url) &&
                  p.prompt_url.some((e: any) => e.status === 1),
              ),
            );
          }
        })
        .catch(console.log);
    }
  }, [data.userId]);

  // Legacy migration: collapse pre-existing detached defaultNode child into inline defaultAction.
  useEffect(() => {
    const existingId = data.defaultNodeId;
    if (!existingId) return;
    const child = getNode(existingId);
    if (child) {
      const migrated = {
        mode: child.data?.mode || '',
        action: child.data?.action || {},
        max_duration: child.data?.max_duration ?? null,
        use_media: !!child.data?.use_media,
      };
      setEdges((eds: any[]) =>
        eds.filter((e: any) => !(e.source === id && e.target === existingId)),
      );
      setNodes((nds: any[]) => nds.filter((n: any) => n.id !== existingId));
      updateNodeData(id, { defaultAction: migrated, defaultNodeId: null });
      setDraftDefaultMode(migrated.mode);
      setDraftDefaultAction(migrated.action);
      setDraftDefaultCategory('general');
      setDraftDefaultDuration(migrated.max_duration || '');
      setDraftDefaultUseMedia(!!migrated.use_media);
    } else {
      updateNodeData(id, { defaultNodeId: null });
    }
  }, []);

  const resetDraftDefaultFromData = () => {
    const d = data.defaultAction || {};
    setDraftDefaultMode(d.mode || '');
    setDraftDefaultAction(d.action || {});
    setDraftDefaultLanguage((d.action || {}).language || '');
    setDraftDefaultCategory('general');
    setDraftDefaultDuration(d.max_duration || '');
    setDraftDefaultUseMedia(Boolean(d.use_media));
    setDraftDefaultSilenceStop(d.recordSilenceStop ?? 0);
    setDraftDefaultStopDtmf(d.recordStopDtmf ?? 'any_dtmf');
    setDefaultErrors({});
  };

  const openModal = () => {
    const _answerCodes =
      getNodes().find((n: any) => n.type === 'answerNode')?.data?.languages ||
      [];
    const _langNode = findUpstreamLanguageNode(id, getNodes, getEdges);
    const _langCodes =
      _langNode &&
      Array.isArray(_langNode.data?.selectedLanguages) &&
      _langNode.data.selectedLanguages.length > 0
        ? _langNode.data.selectedLanguages
        : null;
    setHasLangParent(!!_langNode);
    setAnswerLangCodes(_langCodes ?? _answerCodes);
    resetDraftDefaultFromData();
    setModalIsOpen(true);
  };

  const handleDefaultModeChange = (e: any) => {
    const m = e.target.value;
    setDraftDefaultMode(m);
    setDraftDefaultAction({});
    setDraftDefaultCategory('');
    setDraftDefaultDuration('');
    setDraftDefaultUseMedia(false);
    setDraftDefaultSilenceStop(0);
    setDraftDefaultStopDtmf('any_dtmf');
  };

  const handleDefaultSilenceStopChange = (e: any) => {
    const value = e.target.value;
    if (value === '' || (/^\d+$/.test(value) && Number(value) <= 10)) {
      setDraftDefaultSilenceStop(value);
    }
  };

  const handleDefaultStopDtmfChange = (e: any) => {
    setDraftDefaultStopDtmf(e.target.value);
  };

  const handleDefaultUseMediaToggle = (e: any) => {
    const checked = e.target.checked;
    setDraftDefaultUseMedia(checked);
    if (!checked) {
      setDraftDefaultCategory('');
      setDraftDefaultAction({});
    }
  };

  const handleDefaultAudioChange = (e: any) => {
    const selected = promptList.find(
      (a: any) => a.id.toString() === e.target.value,
    );
    if (!selected) {
      setDraftDefaultAction((prev: any) => ({
        ...prev,
        id: '',
        audio: '',
        name: '',
        duration: 0,
      }));
      setDraftDefaultLanguage('');
      return;
    }
    const approvedLangs = Array.isArray(selected.prompt_url)
      ? selected.prompt_url.filter(
          (en: any) =>
            en.status === 1 &&
            (answerLangCodes.length === 0 ||
              answerLangCodes.includes(en.language)),
        )
      : [];

    if (hasLangParent) {
      // Downstream of a Language node — keep every approved language variant, no manual pick.
      setDraftDefaultAction({
        type: draftDefaultMode,
        prompt_category: 'general',
        id: selected.id,
        name: selected.prompt_name,
        duration: selected.prompt_duration || 0,
        languages: approvedLangs
          .filter((en: any) => en.status === 1)
          .map((en: any) => ({
            language: en.language,
            url: en.url,
            duration: en.duration ?? selected.prompt_duration ?? 0,
            status: en.status,
          })),
      });
      setDraftDefaultLanguage('');
      return;
    }

    const autoLang = approvedLangs.length === 1 ? approvedLangs[0].language : '';
    const autoAudio = approvedLangs.length === 1 ? approvedLangs[0].url : '';
    setDraftDefaultAction({
      type: draftDefaultMode,
      prompt_category: 'general',
      id: selected.id,
      audio: autoAudio,
      duration: selected.prompt_duration || 0,
      name: selected.prompt_name,
      language: autoLang,
    });
    setDraftDefaultLanguage(autoLang);
  };

  const handleDefaultLanguageChange = (e: any) => {
    const lang = e.target.value;
    setDraftDefaultLanguage(lang);
    const sel = promptList.find(
      (a: any) => a.id?.toString() === draftDefaultAction?.id?.toString(),
    );
    const entry =
      lang && sel && Array.isArray(sel.prompt_url)
        ? sel.prompt_url.find((en: any) => en.language === lang)
        : null;
    setDraftDefaultAction((prev: any) => ({
      ...prev,
      audio: entry?.url || '',
      language: lang,
    }));
  };

  const handleDefaultDurationChange = (e: any) => {
    const val = e.target.value;
    if (
      /^\d*$/.test(val) &&
      (val === '' || Number(val) <= VOICEMAIL_MAX_DURATION)
    ) {
      setDraftDefaultDuration(val);
    }
  };

  const validateDefault = (fullDay: boolean) => {
    if (fullDay) return true;
    const newErrors: any = {};
    if (!draftDefaultMode) newErrors.mode = 'Action required';
    if (draftDefaultMode === 'hangup_with_media') {
      if (draftDefaultCategory && !draftDefaultAction?.id) {
        newErrors.audio = 'Audio required when category selected';
      }
      if (!hasLangParent && draftDefaultAction?.id && !draftDefaultLanguage)
        newErrors.language = 'Please select a language';
    }
    if (draftDefaultMode === 'voicemail') {
      if (
        draftDefaultUseMedia &&
        draftDefaultCategory &&
        !draftDefaultAction?.id
      ) {
        newErrors.audio = 'Audio required when category selected';
      }
      if (
        !hasLangParent &&
        draftDefaultUseMedia &&
        draftDefaultAction?.id &&
        !draftDefaultLanguage
      )
        newErrors.language = 'Please select a language';
      if (
        !draftDefaultDuration ||
        Number(draftDefaultDuration) < VOICEMAIL_MIN_DURATION
      ) {
        newErrors.duration = `Min ${VOICEMAIL_MIN_DURATION}s`;
      } else if (Number(draftDefaultDuration) > VOICEMAIL_MAX_DURATION) {
        newErrors.duration = `Max ${VOICEMAIL_MAX_DURATION}s`;
      }
      if (
        draftDefaultSilenceStop === '' ||
        draftDefaultSilenceStop === null ||
        draftDefaultSilenceStop === undefined
      ) {
        newErrors.silenceStop = 'Silence stop is required';
      } else if (
        !/^\d+$/.test(String(draftDefaultSilenceStop)) ||
        Number(draftDefaultSilenceStop) < 0 ||
        Number(draftDefaultSilenceStop) > 10
      ) {
        newErrors.silenceStop = 'Enter an integer from 0 to 10';
      }
      if (
        draftDefaultStopDtmf === '' ||
        draftDefaultStopDtmf === null ||
        draftDefaultStopDtmf === undefined
      ) {
        newErrors.stopDtmf = 'Stop DTMF is required';
      } else if (!/^(any_dtmf|\*|#)$/.test(String(draftDefaultStopDtmf))) {
        newErrors.stopDtmf = 'Must be Any DTMF, * or #';
      }
    }
    setDefaultErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildDefaultActionPayload = () => {
    const payload: any = { mode: draftDefaultMode };
    if (draftDefaultMode === 'hangup') {
      payload.action = {};
      payload.max_duration = null;
      payload.use_media = false;
    } else if (draftDefaultMode === 'voicemail') {
      payload.action = draftDefaultUseMedia ? draftDefaultAction : {};
      payload.max_duration = Number(draftDefaultDuration);
      payload.use_media = draftDefaultUseMedia;
      payload.recordSilenceStop = Number(draftDefaultSilenceStop);
      payload.recordStopDtmf = String(draftDefaultStopDtmf);
    } else {
      payload.action = draftDefaultAction;
      payload.max_duration = null;
      payload.use_media = false;
    }
    return payload;
  };

  const handleSave = () => {
    for (const slot of timeSlots) {
      if (!slot.startTime || !slot.endTime || !slot.type || !slot.transferId) {
        toast.error('Please fill all fields');
        return;
      }
    }

    const fullDay = coverageMins >= FULL_DAY_MINUTES;
    if (!validateDefault(fullDay)) return;

    const finalSlots = timeSlots.map((slot: any) => {
      const sourceList = slot.type === 'group' ? groupList : agentList;
      const selected = sourceList.find(
        (x: any) => x.id.toString() === slot.transferId.toString(),
      );
      return {
        start_time: slot.startTime,
        end_time: slot.endTime,
        type: `transfer_${slot.type}`,
        id: selected?.id,
        name:
          slot.type === 'group' ? selected?.group_name : selected?.agent_name,
      };
    });

    const updates: any = { timeSlots: finalSlots };
    if (fullDay) {
      updates.defaultAction = null;
    } else {
      updates.defaultAction = buildDefaultActionPayload();
    }
    updates.defaultNodeId = null;

    updateNodeData(id, updates);
    toast.success('Details updated successfully.');
    setModalIsOpen(false);
  };

  const handleRemove = () => {
    onRemove(id);
  };

  const displayPromptList =
    answerLangCodes.length === 0
      ? promptList
      : promptList.filter(
          (p: any) =>
            Array.isArray(p.prompt_url) &&
            p.prompt_url.some(
              (e: any) =>
                e.status === 1 && answerLangCodes.includes(e.language),
            ),
        );

  const renderDefaultLangControl = () => {
    const sel = promptList.find(
      (a: any) => a.id?.toString() === draftDefaultAction?.id?.toString(),
    );
    if (!sel) return null;
    if (hasLangParent) {
      const langs = draftDefaultAction?.languages;
      if (!Array.isArray(langs) || langs.length === 0) return null;
      return (
        <div
          style={{
            marginTop: 10,
            fontSize: 12,
            color: '#0f766e',
            background: '#f0fdfa',
            border: '1px solid #99f6e4',
            borderRadius: 8,
            padding: '8px 12px',
          }}
        >
          This node is downstream of a Language node — all {langs.length}{' '}
          approved language{langs.length !== 1 ? 's' : ''} (
          {langs.map((l: any) => l.language?.toUpperCase()).join(', ')}) will
          be saved automatically.
        </div>
      );
    }
    const langs = Array.isArray(sel?.prompt_url)
      ? sel.prompt_url.filter(
          (e: any) =>
            e.status === 1 &&
            (answerLangCodes.length === 0 ||
              answerLangCodes.includes(e.language)),
        )
      : [];
    if (langs.length === 0) return null;
    return (
      <Form.Group style={{ marginTop: 10 }}>
        <Form.Label>
          Select Audio Language <span style={{ color: 'red' }}>*</span>
        </Form.Label>
        <Form.Control
          as="select"
          value={draftDefaultLanguage}
          onChange={handleDefaultLanguageChange}
        >
          <option value="">Select Language</option>
          {langs.map((e: any) => (
            <option key={e.language} value={e.language}>
              {e.language?.toUpperCase()}
              {e.name ? ` — ${e.name}` : ''}
            </option>
          ))}
        </Form.Control>
        {defaultErrors.language && (
          <Form.Text className="text-danger">{defaultErrors.language}</Form.Text>
        )}
      </Form.Group>
    );
  };

  const savedDefaultMode = data.defaultAction?.mode;
  const savedDefaultLabel =
    TRANSFER_DEFAULT_OPTIONS.find((o: any) => o.value === savedDefaultMode)
      ?.label || '';

  const renderListenBtn = (url: any, name: any) =>
    url ? (
      <button
        type="button"
        className="ivr-drawer-play-btn"
        title="Listen"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() =>
          setDrawerPreview({ open: true, url, name: name || 'Audio' })
        }
        style={{ marginLeft: 8 }}
      >
        ▶
      </button>
    ) : null;

  return (
    <div className="ivrn" style={{ '--node-color': '#d97706' } as any}>
      <span
        className="ivrn-icon"
        style={{ background: '#d97706', color: '#fff' }}
        onClick={triggerHandleConnect}
      >
        <PhoneForwarded size={22} strokeWidth={2} />
      </span>
      <Handle
        type="target"
        position={'left' as any}
        style={{ background: '#d97706', top: '65%' }}
      />
      <Handle
        type="source"
        position={'right' as any}
        style={{
          background: '#d97706',
          opacity: 0,
          pointerEvents: 'none',
        }}
        isConnectable={false}
      />

      <div className="ivrn-head">
        <span className="ivrn-title">Transfer</span>
        {savedFullDay && (
          <span className="ivrn-badge ivrn-badge--orange">24/7</span>
        )}
      </div>
      <div className="ivrn-body">
        {savedDefaultLabel ? (
          <span className="ivrn-val">
            <ArrowRight size={10} style={{ marginRight: 4, flexShrink: 0 }} />
            Default: {savedDefaultLabel}
          </span>
        ) : (
          <span className="ivrn-hint">Click ⚙ to set routing</span>
        )}
      </div>

      <div className="ivrn-actions">
        <button
          className="ivrn-btn ivrn-btn--cfg"
          title="Configure"
          onClick={openModal}
        >
          <Settings size={14} strokeWidth={2} />
        </button>
        <button
          className="ivrn-btn ivrn-btn--del"
          title="Remove"
          onClick={handleRemove}
        >
          <Trash2 size={14} strokeWidth={2} />
        </button>
      </div>

      <IvrDrawer
        show={modalIsOpen}
        onHide={() => setModalIsOpen(false)}
        title="Transfer"
        subtitle="Route calls to agents or groups based on time slots."
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalIsOpen(false)}>
              Close
            </Button>
            <Button
              onClick={handleSave}
              disabled={timeSlots.some(
                (slot: any, index: number) =>
                  !slot.startTime ||
                  !slot.endTime ||
                  !slot.type ||
                  !slot.transferId ||
                  isTimeOverlapping(index, slot.startTime, slot.endTime),
              )}
            >
              Save
            </Button>
          </>
        }
      >
        <Form>
          {timeSlots.map((slot: any, index: number) => {
            const overlap = isTimeOverlapping(
              index,
              slot.startTime,
              slot.endTime,
            );
            return (
              <div
                key={index}
                style={{
                  border: '1px solid #dddddd65',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '15px',
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>Time Slot {index + 1}</strong>
                  {timeSlots.length > 1 && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => removeTimeSlot(index)}
                    >
                      Remove
                    </Button>
                  )}
                </div>

                <Form.Check
                  type="checkbox"
                  id={`transfer-full-day-${id}-${index}`}
                  className="mb-2"
                  label="Full day (00:00 – 23:59)"
                  checked={isFullDaySlot(slot)}
                  onChange={(e: any) => setSlotFullDay(index, e.target.checked)}
                />

                <Form.Group className="mb-2">
                  <Form.Label>
                    Start Time <span style={{ color: 'red' }}>*</span>
                  </Form.Label>
                  <CalendarTimeInput
                    value={slot.startTime}
                    disabled={isFullDaySlot(slot)}
                    onChange={(e) =>
                      updateTimeSlot(index, 'startTime', e)
                    }
                  />
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label>
                    End Time <span style={{ color: 'red' }}>*</span>
                  </Form.Label>
                  <CalendarTimeInput
                    value={slot.endTime}
                    disabled={isFullDaySlot(slot)}
                    onChange={(e) =>
                      updateTimeSlot(index, 'endTime', e)
                    }
                  />
                </Form.Group>

                {overlap && (
                  <div
                    style={{
                      color: 'red',
                      fontSize: '12px',
                      marginBottom: '10px',
                    }}
                  >
                    Time slot overlaps with another slot
                  </div>
                )}

                <Form.Group className="mb-2">
                  <Form.Label>
                    Select Type <span style={{ color: 'red' }}>*</span>
                  </Form.Label>
                  <Form.Control
                    as="select"
                    value={slot.type}
                    onChange={(e) =>
                      updateTimeSlot(index, 'type', e.target.value)
                    }
                  >
                    <option value="">Select</option>
                    <option value="group">Call Group</option>
                    <option value="agent">Agent</option>
                  </Form.Control>
                </Form.Group>

                {slot.type === 'group' && (
                  <Form.Group className="mb-2">
                    <Form.Label>
                      Select Group <span style={{ color: 'red' }}>*</span>
                    </Form.Label>
                    <Form.Control
                      as="select"
                      value={slot.transferId}
                      onChange={(e) =>
                        updateTimeSlot(index, 'transferId', e.target.value)
                      }
                    >
                      <option value="">Select Group</option>
                      {groupList.map((g: any) => (
                        <option key={g.id} value={g.id}>
                          {g.group_name}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                )}

                {slot.type === 'agent' && (
                  <Form.Group className="mb-2">
                    <Form.Label>
                      Select Agent <span style={{ color: 'red' }}>*</span>
                    </Form.Label>
                    <Form.Control
                      as="select"
                      value={slot.transferId}
                      onChange={(e) =>
                        updateTimeSlot(index, 'transferId', e.target.value)
                      }
                    >
                      <option value="">Select Agent</option>
                      {agentList.map((agent: any) => (
                        <option key={agent.id} value={agent.id}>
                          {agent.agent_name}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                )}
              </div>
            );
          })}

          {!isFullDay && (
            <Button variant="success" onClick={addTimeSlot}>
              + Add Time Slot
            </Button>
          )}

          <div
            style={{
              marginTop: 20,
              padding: 12,
              background: '#f8f9fa',
              borderRadius: 8,
            }}
          >
            <strong>Day Coverage:</strong> {coverageMins} / {FULL_DAY_MINUTES}{' '}
            mins{' '}
            {isFullDay
              ? '(Full day covered — default not needed)'
              : '(Configure default action below for uncovered time)'}
          </div>

          {!isFullDay && (
            <div
              style={{
                marginTop: 20,
                padding: 12,
                border: '1px solid #ffc107',
                borderRadius: 8,
              }}
            >
              <h6 style={{ marginBottom: 12 }}>
                Default Action (Uncovered Time)
              </h6>

              <Form.Group>
                <Form.Label>
                  Action<Form.Text className="text-danger">*</Form.Text>
                </Form.Label>
                <Form.Control
                  as="select"
                  value={draftDefaultMode}
                  onChange={handleDefaultModeChange}
                >
                  <option value="">Select Action</option>
                  {TRANSFER_DEFAULT_OPTIONS.map((m: any) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </Form.Control>
                {defaultErrors.mode && (
                  <Form.Text className="text-danger">
                    {defaultErrors.mode}
                  </Form.Text>
                )}
              </Form.Group>

              {draftDefaultMode === 'hangup_with_media' && (
                <>
                  <Form.Group style={{ marginTop: 15 }}>
                    <Form.Label>Select Audio</Form.Label>
                    <Form.Control
                      as="select"
                      value={draftDefaultAction?.id || ''}
                      onChange={handleDefaultAudioChange}
                    >
                      <option value="">Select Audio</option>
                      {displayPromptList.map((item: any) => (
                        <option key={item.id} value={item.id}>
                          {item.prompt_name}
                        </option>
                      ))}
                    </Form.Control>
                    {defaultErrors.audio && (
                      <Form.Text className="text-danger">
                        {defaultErrors.audio}
                      </Form.Text>
                    )}
                  </Form.Group>
                  {renderDefaultLangControl()}
                  {renderListenBtn(
                    draftDefaultAction?.audio ||
                      draftDefaultAction?.languages?.[0]?.url ||
                      '',
                    draftDefaultAction?.name,
                  )}
                </>
              )}

              {draftDefaultMode === 'voicemail' && (
                <>
                  <Form.Group style={{ marginTop: 15 }}>
                    <Form.Label>
                      Max Voicemail Duration (sec, {VOICEMAIL_MIN_DURATION}-
                      {VOICEMAIL_MAX_DURATION})
                      <Form.Text className="text-danger">*</Form.Text>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min={VOICEMAIL_MIN_DURATION}
                      max={VOICEMAIL_MAX_DURATION}
                      value={draftDefaultDuration}
                      onChange={handleDefaultDurationChange}
                    />
                    {defaultErrors.duration && (
                      <Form.Text className="text-danger">
                        {defaultErrors.duration}
                      </Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group style={{ marginTop: 15 }}>
                    <Form.Label>
                      Silence Stop (seconds)
                      <Form.Text className="text-danger"> *</Form.Text>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      max="10"
                      step="1"
                      value={draftDefaultSilenceStop}
                      onChange={handleDefaultSilenceStopChange}
                      placeholder="Enter an integer from 0 to 10"
                    />
                    {defaultErrors.silenceStop && (
                      <Form.Text className="text-danger">
                        {defaultErrors.silenceStop}
                      </Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group style={{ marginTop: 15 }}>
                    <Form.Label>
                      Stop DTMF (0 - 9, *, #)
                      <Form.Text className="text-danger"> *</Form.Text>
                    </Form.Label>
                    <Form.Control
                      as="select"
                      value={draftDefaultStopDtmf}
                      onChange={handleDefaultStopDtmfChange}
                    >
                      <option value="any_dtmf">Any DTMF (0 - 9)</option>
                      <option value="*">*</option>
                      <option value="#">#</option>
                    </Form.Control>
                    {defaultErrors.stopDtmf && (
                      <Form.Text className="text-danger">
                        {defaultErrors.stopDtmf}
                      </Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group style={{ marginTop: 15 }}>
                    <Form.Check
                      type="checkbox"
                      id={`def-use-media-${id}`}
                      label="Use Media"
                      checked={draftDefaultUseMedia}
                      onChange={handleDefaultUseMediaToggle}
                    />
                  </Form.Group>

                  {draftDefaultUseMedia && (
                    <>
                      <Form.Group style={{ marginTop: 15 }}>
                        <Form.Label>Select Audio</Form.Label>
                        <Form.Control
                          as="select"
                          value={draftDefaultAction?.id || ''}
                          onChange={handleDefaultAudioChange}
                        >
                          <option value="">Select Audio</option>
                          {displayPromptList.map((item: any) => (
                            <option key={item.id} value={item.id}>
                              {item.prompt_name}
                            </option>
                          ))}
                        </Form.Control>
                        {defaultErrors.audio && (
                          <Form.Text className="text-danger">
                            {defaultErrors.audio}
                          </Form.Text>
                        )}
                      </Form.Group>
                      {renderDefaultLangControl()}
                      {renderListenBtn(
                        draftDefaultAction?.audio ||
                          draftDefaultAction?.languages?.[0]?.url ||
                          '',
                        draftDefaultAction?.name,
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </Form>
      </IvrDrawer>

      <AudioPreviewModal
        show={drawerPreview.open}
        onClose={() =>
          setDrawerPreview({ open: false, url: '', name: '' })
        }
        audioUrl={drawerPreview.url}
        title={drawerPreview.name}
      />
    </div>
  );
};

export default TransferNode;
